import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LayoutDashboard, Car, Users, FileText, CalendarDays,
  Bell, Menu, LogOut, ChevronRight, Check, Wrench, Settings, Sun, Moon, X, Archive, MessageCircle, BarChart3, Globe
} from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/veicoli", label: "Flotta", icon: Car },
  { path: "/clienti", label: "Clienti", icon: Users },
  { path: "/noleggi", label: "Noleggi", icon: FileText },
  { path: "/manutenzione", label: "Manutenzione", icon: Wrench },
  { path: "/calendario", label: "Calendario", icon: CalendarDays },
  { path: "/archivio", label: "Archivio Contratti", icon: Archive },
  { path: "/report", label: "Report", icon: BarChart3 },
  { path: "/sito-web", label: "Sito Web", icon: Globe },
  { path: "/chat", label: "Assistente AI", icon: MessageCircle },
  { path: "/impostazioni", label: "Impostazioni", icon: Settings },
];

const SidebarContent = ({ location, onNavigate, settings }) => {
  const gold = settings?.color_theme === "gold";
  const accent = gold ? "#D9A441" : (settings?.primary_color || "#DC2626");
  return (
  <div className="flex flex-col h-full">
    <div className="p-5 border-b border-slate-800">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden shrink-0" style={{ backgroundColor: accent }}>
          {settings?.app_icon_url ? (
            <img src={settings.app_icon_url} alt="Logo" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <Car className="w-5 h-5 text-white" />
          )}
        </div>
        <div>
          <h2 className="font-outfit font-bold text-white text-base tracking-tight">{settings?.app_name || "Noleggio Veloce"}</h2>
          <p className="text-[11px] text-slate-500 tracking-wide uppercase">{settings?.app_tagline || "Gestione Flotta"}</p>
        </div>
      </div>
    </div>
    <nav className="flex-1 p-3 space-y-1">
      {navItems.map((item) => {
        const active = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              active
                ? "text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-800/60"
            }`}
            style={active ? { backgroundColor: `${accent}20`, color: accent } : {}}
          >
            <item.icon className="w-[18px] h-[18px]" />
            <span>{item.label}</span>
            {active && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  </div>
);
};

export default function AppLayout({ children }) {
  const { user, logout, axiosAuth } = useAuth();
  const { settings } = useSettings();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const api = axiosAuth();
    api.get("/notifications").then((res) => {
      setNotifications(res.data);
      setUnreadCount(res.data.filter((n) => !n.read).length);
    }).catch(() => {});
    api.post("/check-expiring").catch(() => {});
    api.get("/auth/profile").then((res) => setProfile(res.data)).catch(() => {});
  }, [axiosAuth, location.pathname]);

  const markAllRead = async () => {
    const api = axiosAuth();
    await api.put("/notifications/read-all");
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (nid) => {
    try {
      const api = axiosAuth();
      await api.delete(`/notifications/${nid}`);
      setNotifications((prev) => {
        const updated = prev.filter((n) => n.id !== nid);
        setUnreadCount(updated.filter((n) => !n.read).length);
        return updated;
      });
    } catch { /* ignore */ }
  };

  const deleteAllNotifications = async () => {
    try {
      const api = axiosAuth();
      await api.delete("/notifications");
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const currentPage = navItems.find((i) => i.path === location.pathname)?.label || "Dashboard";
  const goldTheme = settings?.color_theme === "gold";
  const sidebarStyle = goldTheme ? { backgroundColor: "#0b0b0c", borderColor: "#26262a" } : {};

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-[240px] bg-slate-950 border-r border-slate-800 fixed inset-y-0 z-40" style={sidebarStyle}>
        <SidebarContent location={location} onNavigate={() => {}} settings={settings} />
        <div className="p-3 border-t border-slate-800">
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-button"
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            <span>Esci</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-[240px] flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-slate-200/60 dark:border-slate-700/60">
          <div className="flex items-center justify-between h-14 px-4 md:px-6">
            <div className="flex items-center gap-3">
              {/* Mobile menu */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    data-testid="mobile-menu-button"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-[260px] bg-slate-950 border-slate-800" style={sidebarStyle}>
                  <SheetTitle className="sr-only">Menu Navigazione</SheetTitle>
                  <SidebarContent location={location} onNavigate={() => setMobileOpen(false)} settings={settings} />
                </SheetContent>
              </Sheet>
              <h1 className="text-lg font-outfit font-bold text-slate-900 dark:text-slate-100" data-testid="page-title">
                {currentPage}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-button"
                className="text-slate-600 dark:text-slate-300"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative z-50"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700" data-testid="notifications-popover">
                  <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700">
                    <span className="font-semibold text-sm text-slate-900 dark:text-white">Notifiche ({notifications.length})</span>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs font-medium" style={{ color: settings?.primary_color || "#DC2626" }} data-testid="mark-all-read-button">
                          Segna lette
                        </button>
                      )}
                      {notifications.length > 0 && (
                        <button onClick={deleteAllNotifications} className="text-xs font-medium text-red-500 hover:text-red-600" data-testid="delete-all-notifications-button">
                          Elimina tutte
                        </button>
                      )}
                    </div>
                  </div>
                  <ScrollArea className="max-h-80">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-slate-500 dark:text-slate-400 text-center">Nessuna notifica</p>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0 text-sm group ${
                            n.read ? "bg-white dark:bg-slate-900" : "bg-blue-50/50 dark:bg-blue-950/20"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.read && <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: settings?.primary_color || "#DC2626" }} />}
                            <p className={`flex-1 ${n.read ? "text-slate-500 dark:text-slate-400" : "text-slate-700 dark:text-slate-200"}`}>
                              {n.message}
                            </p>
                            <button
                              onClick={() => deleteNotification(n.id)}
                              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity shrink-0 mt-0.5"
                              data-testid={`delete-notification-${n.id}`}
                              title="Elimina notifica"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>

              {/* User */}
              <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-slate-200 dark:border-slate-700">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center overflow-hidden">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span className="text-xs font-bold text-white">
                      {(profile?.name || user?.name)?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300" data-testid="user-name">
                  {profile?.name || user?.name || "Utente"}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
